<template>
  <Button
    class="increment-button"
    :class="[stateModifier, { 'increment-button--compact': compact, 'increment-button--disabled': disabled }]"
    :disabled="disabled"
  >
    <span class="increment-button__amount">{{ sign }}${{ amount.toLocaleString() }}</span>
    <span v-if="hint" class="increment-button__hint">{{ hint }}</span>
  </Button>
</template>

<script setup>
import { computed } from 'vue';
import Button from 'primevue/button';

// One step on the right-hand price ladder.
//
// Two tones, because the ladder means two different things depending on where
// the lot is: once bids are coming in it steps the increment UP in the brand
// mint, and while the auctioneer is still fishing for an opening bid it drops
// the ask, which is blue so the clerk cannot confuse the two directions.
//
// Three states, and they are ordered: `suggested` (a solid fill — take this
// one) beats `active` (the step currently set) beats the resting step.
//
// The whole tone/state palette used to be computed into an inline style object,
// which put nine colors in a script where no theme could reach them. It is
// modifier classes and tokens now; the pairs are spelled as ONE modifier
// (`--active-info`) rather than two, so each rule derives an unambiguous token
// path.
defineOptions({ name: 'IncrementButton' });

const props = defineProps({
  amount: { type: Number, required: true },
  // Short uppercase flag on the right — "Current" on the set step.
  hint: { type: String, default: null },
  // The increment currently set on the lot.
  active: { type: Boolean, default: false },
  // The step the console is recommending. Outranks `active`.
  suggested: { type: Boolean, default: false },
  // comfortable | compact
  density: { type: String, default: 'comfortable', validator: (value) => ['comfortable', 'compact'].includes(value) },
  // The sign printed before the amount: '+' stepping up, '-' dropping the ask.
  sign: { type: String, default: '+' },
  // primary (bidding up) | info (dropping the ask)
  tone: { type: String, default: 'primary', validator: (value) => ['primary', 'info'].includes(value) },
  disabled: { type: Boolean, default: false },
});

const compact = computed(() => props.density === 'compact');

const stateModifier = computed(() => {
  if (props.suggested) return `increment-button--suggested-${props.tone}`;
  if (props.active) return `increment-button--active-${props.tone}`;
  return null;
});
</script>

<style scoped>
/* The `.p-button` prefix is load-bearing: Aura sets a background on its own
   base class, and without out-ranking it the state fills below never paint. */
.p-button.increment-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
  font-family: inherit;
  transition: all 120ms;
  background: var(--p-increment-button-background);
  color: var(--p-increment-button-color);
  border: var(--p-increment-button-border-width) solid var(--p-increment-button-border-color);
  border-radius: var(--p-increment-button-border-radius);
  padding: var(--p-increment-button-padding);
  box-shadow: var(--p-increment-button-box-shadow);
}

.p-button.increment-button:hover {
  background: var(--p-increment-button-hover-background);
}

.p-button.increment-button--compact {
  padding: var(--p-increment-button-compact-padding);
}

.p-button.increment-button--disabled {
  opacity: 0.42;
  cursor: not-allowed;
}

/* ── The set step ───────────────────────────────────────────────────────── */

.p-button.increment-button--active-primary,
.p-button.increment-button--active-primary:hover {
  background: var(--p-increment-button-active-primary-background);
  color: var(--p-increment-button-active-primary-color);
  border: var(--p-increment-button-active-border-width) solid var(--p-increment-button-active-primary-border-color);
  box-shadow: var(--p-increment-button-active-primary-box-shadow);
}

.p-button.increment-button--active-info,
.p-button.increment-button--active-info:hover {
  background: var(--p-increment-button-active-info-background);
  color: var(--p-increment-button-active-info-color);
  border: var(--p-increment-button-active-border-width) solid var(--p-increment-button-active-info-border-color);
  box-shadow: var(--p-increment-button-active-info-box-shadow);
}

/* ── The recommended step ───────────────────────────────────────────────── */

.p-button.increment-button--suggested-primary,
.p-button.increment-button--suggested-primary:hover {
  background: var(--p-increment-button-suggested-primary-background);
  color: var(--p-increment-button-suggested-primary-color);
  border-color: var(--p-increment-button-suggested-primary-border-color);
  box-shadow: var(--p-increment-button-suggested-primary-box-shadow);
}

.p-button.increment-button--suggested-info,
.p-button.increment-button--suggested-info:hover {
  background: var(--p-increment-button-suggested-info-background);
  color: var(--p-increment-button-suggested-info-color);
  border-color: var(--p-increment-button-suggested-info-border-color);
  box-shadow: var(--p-increment-button-suggested-info-box-shadow);
}

/* ── Parts ──────────────────────────────────────────────────────────────── */

.increment-button__amount {
  font-family: var(--p-increment-button-amount-font-family);
  font-weight: var(--p-increment-button-amount-font-weight);
  letter-spacing: var(--p-increment-button-amount-letter-spacing);
  font-size: var(--p-increment-button-amount-font-size);
  font-variant-numeric: tabular-nums;
}

.increment-button--compact .increment-button__amount {
  font-size: var(--p-increment-button-compact-amount-font-size);
}

.increment-button__hint {
  font-size: var(--p-increment-button-hint-font-size);
  font-weight: var(--p-increment-button-hint-font-weight);
  letter-spacing: var(--p-increment-button-hint-letter-spacing);
  padding: var(--p-increment-button-hint-padding);
  border-radius: var(--p-increment-button-hint-border-radius);
  background: var(--p-increment-button-hint-background);
  color: var(--p-increment-button-hint-color);
  text-transform: uppercase;
}

.increment-button--active-primary .increment-button__hint {
  background: var(--p-increment-button-active-primary-hint-background);
  color: var(--p-increment-button-active-primary-hint-color);
}

.increment-button--active-info .increment-button__hint {
  background: var(--p-increment-button-active-info-hint-background);
  color: var(--p-increment-button-active-info-hint-color);
}
</style>
